import { useState, type CSSProperties, type ReactNode } from 'react'
import type { CommandSummary, ModelStateItem, NodeAvailableModel } from '@/types'
import styles from './nodeDetail.module.css'
import { cx, relativeTime } from './utils'

const OPENVINO_FORMATS = new Set(['openvino_int8', 'openvino_fp16', 'openvino_fp32'])

interface ModelDeployPanelProps {
  states: ModelStateItem[]
  availableModels: NodeAvailableModel[]
  commands: CommandSummary[]
  loading: boolean
  error: string | null
  canMapModel: boolean
  canDeleteMapping: boolean
  canDeployModel: boolean
  canRefreshStatus: boolean
  supportsModelDeploy: boolean
  headerActions?: ReactNode
  onRefreshSlot: (slotKey: string | null) => Promise<void>
  onMapModel: (cameraKey: string, modelArtifactId: string) => Promise<void>
  onDeleteMapping: (cameraKey: string) => Promise<void>
  onDeploy: (state: ModelStateItem) => Promise<void>
}

function actionButtonVars(color: string): CSSProperties {
  return { '--camera-action-color': color } as CSSProperties
}

export function ModelDeployPanel({
  states,
  availableModels,
  commands,
  loading,
  error,
  canMapModel,
  canDeleteMapping,
  canDeployModel,
  canRefreshStatus,
  supportsModelDeploy,
  headerActions,
  onRefreshSlot,
  onMapModel,
  onDeleteMapping,
  onDeploy,
}: ModelDeployPanelProps) {
  const [busyKeys, setBusyKeys] = useState<Set<string>>(() => new Set())
  const [message, setMessage] = useState<string | null>(null)

  const latestModelCommands = commands.filter((cmd) => cmd.type.startsWith('model.')).slice(0, 5)
  const availableModelGroups = availableModels.reduce<Map<string, NodeAvailableModel[]>>((groups, model) => {
    const models = groups.get(model.model_key)
    if (models) {
      models.push(model)
    } else {
      groups.set(model.model_key, [model])
    }
    return groups
  }, new Map())

  async function runAction(key: string, action: () => Promise<void>, success: string) {
    setBusyKeys((current) => new Set(current).add(key))
    setMessage(null)
    try {
      await action()
      setMessage(success)
    } catch (e: unknown) {
      const err = e as { message?: string }
      setMessage(err?.message ?? 'Model deployment action failed.')
    } finally {
      setBusyKeys((current) => {
        const next = new Set(current)
        next.delete(key)
        return next
      })
    }
  }

  function artifactLabel(artifact: NodeAvailableModel): string {
    return `${artifact.model_key} / v${artifact.version}${artifact.format ? ` · ${artifact.format}` : ''}`
  }

  function selectedArtifact(state: ModelStateItem): NodeAvailableModel | null {
    return availableModels.find((artifact) => artifact.model_artifact_id === state.desired_model_artifact_id) ?? null
  }

  function safePart(value: string): string {
    return value.replace(/\//g, '_')
  }

  function slotTargetAfterDeploy(state: ModelStateItem): string {
    const artifact = selectedArtifact(state)
    if (!artifact) return '—'
    const safeKey = safePart(artifact.model_key)
    const safeVersion = safePart(artifact.version)
    if (artifact.format && OPENVINO_FORMATS.has(artifact.format)) {
      const keyWithoutSuffix = safeKey.endsWith('_openvino_model')
        ? safeKey.slice(0, -'_openvino_model'.length)
        : safeKey
      return `${keyWithoutSuffix}__v${safeVersion}_openvino_model`
    }
    if (artifact.format === 'pt') {
      return `${safeKey}__v${safeVersion}/<payload>.pt`
    }
    return `${safeKey}__v${safeVersion}`
  }

  function slotConfigPath(state: ModelStateItem): string {
    return state.weight_path ?? '—'
  }

  async function deployWithConfirm(state: ModelStateItem) {
    const impact = state.impact_camera_keys.length > 0 ? state.impact_camera_keys.join(', ') : state.camera_key
    const ok = window.confirm([
      `Deploy model for ${state.camera_key}?`,
      `Impact cameras: ${impact}`,
      `Current: ${state.current_model_key ? `${state.current_model_key} / v${state.current_version}` : 'unknown'}`,
      `Desired: ${state.desired_model_key ? `${state.desired_model_key} / v${state.desired_version}` : 'not mapped'}`,
      `Config path: ${slotConfigPath(state)}`,
      `Slot target after deploy: ${slotTargetAfterDeploy(state)}`,
      'Model/config names and predictor compatibility are not automatically verified; verify on site after deployment.',
    ].join('\n'))
    if (!ok) return
    await runAction(`deploy:${state.camera_key}`, () => onDeploy(state), 'Deployment queued.')
  }

  return (
    <div className={styles.modelPanelStack}>
      {!supportsModelDeploy && (
        <div className={styles.modelWarning}>Agent update required before model deployment commands can run.</div>
      )}
      {error && <div className={styles.autoUploadError}>{error}</div>}
      {message && <div className={styles.modelInfo}>{message}</div>}

      <div className={styles.modelSummaryGrid}>
        <div className={styles.card}>
          <div className={styles.cardTitle}>Catalog</div>
          <div className={styles.modelMetric}>{availableModels.length}</div>
          <div className={styles.modelMutedText}>Models assigned to this node</div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardTitle}>Restart Required</div>
          <div className={styles.modelMetric}>{states.filter((state) => state.deployment_status === 'restart_required').length}</div>
          <div className={styles.modelMutedText}>Slots waiting for GuardianAlpha restart</div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.modelSectionHeader}>
          <div className={cx(styles.cardTitle, styles.cardTitleInline)}>Camera Model Mapping</div>
          <div className={styles.modelHeaderActions}>
            {loading && <span className={styles.modelMutedText}>Loading...</span>}
            {headerActions}
          </div>
        </div>
        {states.length === 0 ? (
          <p className={styles.modelEmptyText}>No model deploy cameras materialized for this node.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.modelTable}>
              <thead>
                <tr>
                  {['Camera', 'Readiness', 'Current', 'Desired mapping', 'Slot / impact', 'Status', 'Actions'].map((heading) => (
                    <th key={heading} className={styles.modelTableHeader}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {states.map((state, index) => {
                  const mappingBusy = busyKeys.has(`map:${state.camera_key}`)
                    || busyKeys.has(`unmap:${state.camera_key}`)
                  const deployBusy = busyKeys.has(`deploy:${state.camera_key}`)
                  const deployDisabled = loading
                    || !canDeployModel
                    || state.readiness !== 'safe'
                    || !state.desired_model_artifact_id
                    || state.deployment_status === 'deploying'
                    || state.deployment_status === 'restart_required'
                    || mappingBusy
                    || deployBusy
                  const refreshDisabled = loading
                    || !canRefreshStatus
                    || !supportsModelDeploy
                    || !state.slot_key
                    || busyKeys.has(`refresh:${state.slot_key}`)
                  const selectedArtifactId = state.desired_model_artifact_id ?? ''
                  return (
                    <tr key={`${state.camera_key}:${state.slot_key ?? index}`}>
                      <td className={styles.modelTableCell}>
                        <div className={styles.modelCameraKey}>{state.camera_key}</div>
                        <div className={styles.modelMutedText}>{state.camera_name ?? '—'}</div>
                      </td>
                      <td className={styles.modelTableCell}>
                        <ReadinessBadge readiness={state.readiness} />
                        {state.readiness_reason && <div className={styles.modelReason}>{state.readiness_reason}</div>}
                      </td>
                      <td className={styles.modelTableCell}>
                        <ModelVersionText modelKey={state.current_model_key} version={state.current_version} />
                      </td>
                      <td className={styles.modelTableCell}>
                        <div className={styles.modelMappingControl}>
                          <select
                            aria-label={`Model mapping for ${state.camera_key}`}
                            value={selectedArtifactId}
                            disabled={!canMapModel || loading || mappingBusy || deployBusy}
                            onChange={(event) => {
                              const artifactId = event.target.value
                              if (!artifactId) return
                              runAction(
                                `map:${state.camera_key}`,
                                () => onMapModel(state.camera_key, artifactId),
                                'Model mapping saved.',
                              )
                            }}
                            className={styles.modelSelect}
                          >
                            <option value="">
                              {availableModels.length === 0 ? 'No assigned models' : 'Select model'}
                            </option>
                            {[...availableModelGroups.entries()].map(([modelKey, models]) => (
                              <optgroup key={modelKey} label={modelKey}>
                                {models.map((artifact) => (
                                  <option
                                    key={artifact.model_artifact_id}
                                    value={artifact.model_artifact_id}
                                  >
                                    {artifactLabel(artifact)}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          {state.desired_model_artifact_id && (
                            <button
                              type="button"
                              aria-label={`Unmap model from ${state.camera_key}`}
                              disabled={!canDeleteMapping || loading || mappingBusy || deployBusy}
                              onClick={() => runAction(
                                `unmap:${state.camera_key}`,
                                () => onDeleteMapping(state.camera_key),
                                'Model mapping removed.',
                              )}
                              className={styles.cameraActionButton}
                              style={actionButtonVars('#c62828')}
                            >
                              Unmap
                            </button>
                          )}
                        </div>
                        <div className={styles.modelReason}>Config path: {slotConfigPath(state)}</div>
                        <div className={styles.modelReason}>Target: {slotTargetAfterDeploy(state)}</div>
                      </td>
                      <td className={styles.modelTableCell}>
                        <code className={styles.modelCode}>{state.slot_key ?? '—'}</code>
                        <div className={styles.modelMutedText}>
                          {state.impact_camera_keys.length > 1
                            ? `Shared: ${state.impact_camera_keys.join(', ')}`
                            : 'Single camera slot'}
                        </div>
                      </td>
                      <td className={styles.modelTableCell}>
                        <DeploymentBadge status={state.deployment_status} />
                        {state.pending_command_id && (
                          <div className={styles.modelReason}>Command {state.pending_command_id.slice(0, 8)}</div>
                        )}
                      </td>
                      <td className={styles.modelTableCell}>
                        <div className={styles.cameraActionGroup}>
                          <button
                            type="button"
                            disabled={refreshDisabled}
                            onClick={() => runAction(
                              `refresh:${state.slot_key}`,
                              () => onRefreshSlot(state.slot_key),
                              'Status fetch queued.',
                            )}
                            className={styles.cameraActionButton}
                            style={actionButtonVars('#546e7a')}
                          >
                            Check
                          </button>
                          <button
                            type="button"
                            disabled={deployDisabled}
                            onClick={() => deployWithConfirm(state)}
                            className={styles.cameraActionButton}
                            style={actionButtonVars('#3949ab')}
                          >
                            Deploy
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.card}>
        <div className={styles.cardTitle}>Recent Model Commands</div>
        {latestModelCommands.length === 0 ? (
          <p className={styles.modelCommandEmptyText}>No model commands yet</p>
        ) : (
          <div className={styles.modelCommandList}>
            {latestModelCommands.map((cmd) => (
              <div key={cmd.id} className={styles.modelCommandRow}>
                <code className={styles.modelCode}>{cmd.type}</code>
                <span>{cmd.status}</span>
                <span className={styles.modelMutedText}>{relativeTime(cmd.created_at)}</span>
                {cmd.log_tail && <span className={styles.modelMutedText}>{cmd.log_tail.slice(0, 120)}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function ModelVersionText({ modelKey, version }: { modelKey: string | null; version: string | null }) {
  if (!modelKey) return <span className={styles.modelMutedText}>—</span>
  return (
    <div>
      <div className={styles.modelNameText}>{modelKey}</div>
      <div className={styles.modelMutedText}>v{version ?? '—'}</div>
    </div>
  )
}

function ReadinessBadge({ readiness }: { readiness: ModelStateItem['readiness'] }) {
  const badgeClass = readiness === 'safe'
    ? styles.modelBadgeSafe
    : readiness === 'blocked'
      ? styles.modelBadgeBlocked
      : styles.modelBadgeManual
  return <span className={cx(styles.modelBadge, badgeClass)}>{readiness ?? 'manual-map'}</span>
}

function DeploymentBadge({ status }: { status: string }) {
  const badgeClass = status === 'applied'
    ? styles.modelBadgeSafe
    : status === 'restart_required'
      ? styles.modelBadgeManual
      : status === 'failed'
        ? styles.modelBadgeBlocked
        : null
  return <span className={cx(styles.modelBadge, badgeClass)}>{status}</span>
}
